import { validate } from '../validators/template-validator';
import type { NormalizedTemplate, ValidationError } from '../types/template';

export type TemplateLoadResult =
  | { ok: true; template: NormalizedTemplate }
  | { ok: false; title: string; errors: ValidationError[] };

/**
 * Fetches a template JSON file from the given URL, parses it,
 * and validates it against the template schema.
 *
 * Returns a discriminated union — either a normalized template
 * or an error with a title and structured error list.
 */
export async function loadTemplate(
  url: string,
  signal?: AbortSignal,
): Promise<TemplateLoadResult> {
  if (!url) {
    return {
      ok: false,
      title: 'Missing Template URL',
      errors: [
        {
          path: 'template-url',
          message: 'The template-url attribute is required',
          code: 'MISSING_TEMPLATE_URL',
        },
      ],
    };
  }

  let response: Response;
  try {
    response = signal ? await fetch(url, { signal }) : await fetch(url);
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw err;
    }
    return {
      ok: false,
      title: 'Unable to Load Template',
      errors: [
        {
          path: 'template-url',
          message: 'Network error while fetching template',
          code: 'TEMPLATE_FETCH_ERROR',
        },
      ],
    };
  }

  if (!response.ok) {
    return {
      ok: false,
      title: 'Unable to Load Template',
      errors: [
        {
          path: 'template-url',
          message: `Failed to fetch template: ${response.status} ${response.statusText}`,
          code: 'TEMPLATE_FETCH_ERROR',
        },
      ],
    };
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return {
      ok: false,
      title: 'Invalid Template File',
      errors: [
        {
          path: 'template-url',
          message: 'Template file contains invalid JSON',
          code: 'TEMPLATE_PARSE_ERROR',
        },
      ],
    };
  }

  const result = validate(parsed);
  if (!result.valid) {
    return {
      ok: false,
      title: 'Template Configuration Error',
      errors: result.errors,
    };
  }

  return { ok: true, template: result.template };
}
